import React, { useEffect } from 'react';
import { Placeholder, VisitorIdentification } from '@sitecore-jss/sitecore-jss-react';
import { withTranslation, useTranslation } from 'react-i18next';
import Helmet from 'react-helmet';
import { SiteProvider } from './contexts/SiteContext';

// Using bootstrap is completely optional. It's used here to provide a clean layout for samples,
// without needing extra CSS in the sample app. Remove it in package.json as well if it's removed here.
import 'bootstrap/dist/css/bootstrap.css';
import './assets/app.css';

/*
  APP LAYOUT
  This is where the app's HTML structure and root placeholders should be defined.

  All routes share this root layout by default (this could be customized in RouteHandler),
  but components added to inner placeholders are route-specific.
*/

const Layout = ({ route }) => {
  const { i18n } = useTranslation();
  const isRTL = i18n.language === 'ar' || i18n.language === 'ar-SA';
  
  // Set document direction based on language
  useEffect(() => {
    if (typeof document !== 'undefined') {
      document.documentElement.setAttribute('dir', isRTL ? 'rtl' : 'ltr');
      document.documentElement.setAttribute('lang', isRTL ? 'ar' : 'en');
    }
  }, [isRTL]);

  // Load Genesys webchat widget based on language
  useEffect(() => {
    if (typeof window !== 'undefined' && typeof document !== 'undefined') {
      // Remove existing Genesys script if language changes
      const existingScript = document.getElementById('genesys-webchat-script');
      if (existingScript) {
        existingScript.remove();
      }
      
      // Reset Genesys object to allow reinitialization with new deployment
      if (window.Genesys) {
        delete window.Genesys;
        delete window._genesysJs;
      }

      // Deployment ID based on language
      const deploymentId = isRTL 
        ? '5ade2cd5-9478-4a76-b3f1-48becd131420'  // Arabic
        : 'b37b6d37-b18c-48e1-81e9-37e067b477b7'; // English

      // Initialize Genesys
      window['_genesysJs'] = 'Genesys';
      window['Genesys'] = window['Genesys'] || function () {
        (window['Genesys'].q = window['Genesys'].q || []).push(arguments);
      };
      window['Genesys'].t = 1 * new Date();
      window['Genesys'].c = {
        environment: 'prod-euc1',
        deploymentId: deploymentId
      };

      // Create and append script
      const script = document.createElement('script');
      script.id = 'genesys-webchat-script';
      script.async = true;
      script.src = 'https://apps.mypurecloud.de/genesys-bootstrap/genesys.min.js';
      script.charset = 'utf-8';
      document.head.appendChild(script);
    }
  }, [isRTL]);

  return (
  <SiteProvider>
    <React.Fragment>
      {/* react-helmet enables setting <head> contents, like title and OG meta tags */}
      <Helmet>
        <title>
          {(route.fields && route.fields.pageTitle && route.fields.pageTitle.value) || 'BMC - Application Form'}
        </title>
      </Helmet>

      {/*
        VisitorIdentification is necessary for Sitecore Analytics to determine if the visitor is a robot.
        If Sitecore XP (with xConnect/xDB) is used, this is required or else analytics will not be collected for the JSS app.
        For XM (CMS-only) apps, this should be removed.

        VI detection only runs once for a given analytics ID, so this is not a recurring operation once cookies are established.
      */}
      <VisitorIdentification />

      {/* root placeholder for the app, which we add components to using route data */}
      <Placeholder name="jss-main" rendering={route} />
    </React.Fragment>
  </SiteProvider>
  );
};

export default Layout;
